import { setDefaultOptions } from "date-fns";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";

import { DEFAULT_NAMESPACE, NAMESPACES } from "./namespaces";
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALE_CODES,
  resolveLocale,
} from "./locales";

/**
 * Mirrors the active language onto the document so CSS, native form controls
 * and screen readers all agree on direction. Runs on init AND on every change,
 * so a persisted RTL choice is applied before first paint of the app shell
 * rather than only when the switcher mounts.
 */
const applyDocumentLanguage = (language: string) => {
  const { code, dir, dateFnsLocale } = resolveLocale(language);
  // The ~150 call sites in `utils/helper` call bare `date-fns` `format()` with no
  // `locale` option, so month/weekday names would stay English in Arabic. Setting
  // the library default here localizes all of them from one place — `useLocale`
  // still passes the locale explicitly, which agrees with this.
  setDefaultOptions({ locale: dateFnsLocale });
  const root = document.documentElement;
  root.setAttribute("lang", code);
  root.setAttribute("dir", dir);
  // Lets Tailwind/CSS target a locale without reading `dir` (e.g. font stacks).
  root.dataset.locale = code;
};

i18n
  .use(HttpApi) // Loads JSON from /public/locales
  .use(LanguageDetector) // localStorage → <html lang> → navigator
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    // Only ever resolve to a locale we actually ship a folder for; without this
    // an `ar-AE` browser would request a nonexistent `/locales/ar-AE/*`.
    supportedLngs: SUPPORTED_LOCALE_CODES,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    ns: NAMESPACES,
    defaultNS: DEFAULT_NAMESPACE,
    // Chrome (nav, buttons, validation) is needed on every screen — load it up
    // front so no screen flashes raw keys while a namespace is in flight.
    preload: [DEFAULT_LOCALE],
    debug: false,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // An explicit choice wins over the browser's guess, and survives reloads.
      order: ["localStorage", "htmlTag", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
    },
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    react: {
      useSuspense: false, // keeps the app rendering while JSON loads
    },
  });

applyDocumentLanguage(i18n.language);
i18n.on("languageChanged", applyDocumentLanguage);

export default i18n;
export * from "./locales";
export * from "./namespaces";
